/**
 * React hook that loads available appointment slots for a visible calendar month and exposes
 * date/time picker constraints plus validation for the currently selected interval.
 */

import { useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useFindAvailableSlotsQuery, type ResourcePolicy } from '@/api/mcp-appointments';
import { buildBookableRange, getLatestBookableDay } from './utils';
import { useSlotScheduleCore } from './useSlotScheduleCore';

/** Inputs that drive slot loading, picker disabling, and selection validation. */
interface UseAvailableSlotsScheduleProps {
  /** Resource whose availability is being queried. */
  resourceId: string | null | undefined;
  /** Selected appointment start; used to validate against returned slots. */
  startTime: Dayjs | null | undefined;
  /** Derived appointment end from start time and duration. */
  endTime: Dayjs | null | undefined;
  /** Policy bounds such as lead time and effective dates. */
  resourcePolicy: ResourcePolicy | undefined;
  /** Month currently shown in the slot calendar. */
  visibleMonth: Dayjs;
  /** Whether the user has committed a concrete start time (not just a day). */
  hasCommittedStartTime: boolean;
  /** Appointment length in minutes; forwarded to available-slots so returned pairs match validation. */
  durationMinutes: number;
  /** Existing slot that should be treated as valid even if absent from fresh results (e.g. reschedule). */
  treatAsValidSlot?: { start: Dayjs; end: Dayjs } | null;
  /** When `false`, skips the availability query entirely. */
  enabled?: boolean;
}

const MAX_SLOTS_REQUEST = 50000;

/** Builds the ISO range for a slot query clipped to policy and the visible month. */
function buildMonthRange(visibleMonth: Dayjs, resourcePolicy: ResourcePolicy | undefined): {
  startTime: string;
  endTime: string;
} | null {
  return buildBookableRange(
    visibleMonth.startOf('month'),
    visibleMonth.endOf('month'),
    resourcePolicy
  );
}

/**
 * Fetches slots for the visible month and derives picker disable callbacks and selection state.
 * @returns Disabled date/time handlers, loading flags, validation outcome, and indexed slot data.
 */
export const useAvailableSlotsSchedule = ({
  resourceId,
  startTime,
  endTime,
  resourcePolicy,
  visibleMonth,
  hasCommittedStartTime,
  durationMinutes,
  treatAsValidSlot = null,
  enabled = true
}: UseAvailableSlotsScheduleProps) => {
  const latestBookableDay = useMemo(() => getLatestBookableDay(resourcePolicy), [resourcePolicy]);

  const queryParams = useMemo(() => {
    if (!resourceId) {
      return null;
    }

    const range = buildMonthRange(visibleMonth, resourcePolicy);
    if (!range) {
      return null;
    }

    return {
      startTime: range.startTime,
      endTime: range.endTime,
      maxSlots: MAX_SLOTS_REQUEST,
      ...(durationMinutes > 0 ? { durationMinutes } : {})
    };
  }, [resourceId, visibleMonth, resourcePolicy, durationMinutes]);

  const { data, isLoading, isFetching } = useFindAvailableSlotsQuery(
    resourceId || '',
    queryParams,
    enabled && !!resourceId && !!queryParams
  );

  const timezone = data?.timezone || 'UTC';
  const slotAlignmentMinutes =
    data?.slotAlignmentMinutes ?? resourcePolicy?.slotAlignmentMinutes ?? 15;
  const truncated = data?.truncated ?? false;

  const disabledDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) {
        return false;
      }

      const day = current.startOf('day');

      if (day.isBefore(dayjs(), 'day')) {
        return true;
      }

      if (resourcePolicy?.effectiveFrom && day.isBefore(dayjs(resourcePolicy.effectiveFrom), 'day')) {
        return true;
      }

      if (latestBookableDay && day.isAfter(latestBookableDay, 'day')) {
        return true;
      }

      return false;
    },
    [resourcePolicy, latestBookableDay]
  );

  const schedule = useSlotScheduleCore({
    slots: data?.slots,
    isLoading,
    isFetching,
    hasSlotsResult: !!data,
    timezone,
    slotAlignmentMinutes,
    visibleMonth,
    startTime,
    endTime,
    hasCommittedStartTime,
    treatAsValidSlot,
    disabledDate
  });

  return { ...schedule, truncated };
};
